import { Stack, Theme, Toolbar, Box } from '@mui/material';
import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import useApp from '../../hooks/use-app.hook';
import Text from '../text';
import Breadcrumbs from './breadcrumbs';
import Loader from '../loader';
import UserMenu from './user-menu';
import AppLogo from '../app-logo';

interface Props {
  signedIn: boolean;
  hasBanner?: boolean;
}

export const APP_HEADER_HEIGHT = 64; // affects height rules in app-header and app-sidebar. if you change this, you MUST change those

const useStyles = createUseStyles<string, { signedIn: boolean }>((theme: Theme) => ({
  header: ({ signedIn }) => ({
    backgroundColor: signedIn ? theme.palette.background.app : theme.palette.common.white,
    width: '100%',
    height: 64,
    minHeight: 64,
    boxSizing: 'border-box',
    overflow: 'hidden',
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
  headerInner: {
    paddingRight: 24,
  },
  pageTitle: {
    flexGrow: 1,
    color: theme.palette.common.black,
  },
  sidebarButton: {
    marginRight: 30,
  },
  logoLink: {
    '&:hover': {
      opacity: 1,
    },
  },
  userMenu: {
    padding: 0,
    transition: theme.transitions.create('padding', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '& > .MuiFab-root': {
      boxShadow: 'none',
      background: 'none',
    },
    '& .MuiSpeedDial-actions': {
      paddingBottom: 36,
    },
  },
}));

const AppHeader: FC<Props> = ({ signedIn }) => {
  const styles = useStyles({ signedIn });
  const { pageTitle, pageLoadState } = useApp();
  const { breadcrumb, plain } = pageTitle;
  const hasCrumbs = Boolean(breadcrumb);
  const pageLoading = pageLoadState === 'loading';

  return (
    <Box className={styles.header}>
      <Toolbar className={styles.headerInner}>
        {!signedIn && (
          <Box width={150}>
            <a href="/" className={styles.logoLink}>
              <AppLogo />
            </a>
          </Box>
        )}
        {signedIn && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            className={styles.pageTitle}
            justifyContent="flex-start"
            width="100%"
          >
            {hasCrumbs && <Breadcrumbs breadcrumb={breadcrumb!} pageTitle={plain} />}
            {!hasCrumbs && (
              <Text size="x-large" noWrap>
                {plain}
              </Text>
            )}
            <Stack
              flexGrow={1}
              flexDirection="row"
              justifyContent="flex-end"
              gap={3}
              alignItems="center"
            >
              <Box width={50}>{pageLoading && <Loader />}</Box>

              {signedIn && <UserMenu />}
            </Stack>
          </Stack>
        )}
      </Toolbar>
    </Box>
  );
};

export default AppHeader;
